/**
 * Avatar Context (Web)
 *
 * Chức năng: React Context quản lý avatar của user cho web app
 * Dùng để:
 * - Lưu trữ avatar URI được chọn từ profile
 * - Chia sẻ avatar giữa các component (Header, Profile, etc.)
 * - Persist avatar vào localStorage
 * - Tự động load avatar từ user profile khi khởi động
 * - Đồng bộ với mobile app qua API
 */
import React, { createContext, useContext, useState, useEffect } from "react";
import { usersService } from "../services/users.service";
import { API_URL } from "../config/constants";
import { useAuth } from "./AuthContext";
import { STORAGE_KEYS } from "@shared/constants";

interface AvatarContextType {
  avatarUri: string | null;
  isLoading: boolean;
  setAvatarUri: (uri: string | null) => Promise<void>;
  refreshAvatar: () => Promise<void>;
}

const AvatarContext = createContext<AvatarContextType | undefined>(undefined);

// Helper function để tạo storage key theo user ID
const getAvatarStorageKey = (userId: string | null | undefined): string => {
  if (!userId) {
    return "hrm_avatar_uri"; // Fallback key nếu không có user ID
  }
  return `hrm_avatar_uri_${userId}`;
};

// Helper function để build full URL từ relative URL
// Convert /public/avatars/... thành /upload/file/avatars/... (endpoint serve file của backend)
export const buildFullUrl = (url: string | null | undefined): string | null => {
  if (!url) return null;

  // Nếu đã là full URL (bắt đầu với http:// hoặc https://)
  if (url.startsWith("http://") || url.startsWith("https://")) {
    // Extract path từ URL
    let path: string;
    try {
      const urlObj = new URL(url);
      const hostname = urlObj.hostname.toLowerCase();

      // Nếu là domain khác (ví dụ pm.sdtc.vn) thì giữ nguyên URL cũ, không convert
      // Chỉ convert khi là HRM domain hoặc localhost (dev)
      const isHrmDomain =
        hostname.includes("hrm.sdtc.vn") ||
        hostname === "localhost" ||
        hostname.startsWith("127.0.0.1");
      if (!isHrmDomain) {
        return url;
      }

      path = urlObj.pathname;
    } catch {
      // Nếu không parse được URL, thử extract path thủ công
      const pathMatch = url.match(/\/public\/.*/);
      if (pathMatch) {
        path = pathMatch[0];
      } else {
        return url;
      }
    }

    // Convert /public/avatars/... hoặc /api/public/avatars/... thành /api/upload/file/avatars/...
    // Backend serve files qua endpoint /api/upload/file/:subfolder/:fileName
    // Handle cả path có /api/ prefix và không có
    let pathToConvert = path;
    const hasApiPrefix = path.startsWith("/api/");
    if (hasApiPrefix) {
      pathToConvert = path.replace("/api", ""); // Remove /api để check /public/
    }
    
    if (pathToConvert.startsWith("/public/")) {
      const pathWithoutPublic = pathToConvert.replace("/public/", "");
      const pathParts = pathWithoutPublic.split("/");
      if (pathParts.length >= 2) {
        const subfolder = pathParts[0]; // avatars, cv, id-cards, etc.
        const fileName = pathParts.slice(1).join("/"); // filename có thể có subfolder
        const newPath = `/api/upload/file/${subfolder}/${fileName}`;
        
        // Build lại URL: lấy origin từ URL gốc + newPath
        const urlObj = new URL(url);
        return `${urlObj.origin}${newPath}`;
      }
    }

    // Nếu không convert được, trả về URL gốc
    return url;
  }

  // Nếu là relative URL, convert /public/... thành /api/upload/file/...
  let cleanPath = url.startsWith("/") ? url : `/${url}`;

  // Convert /public/avatars/... thành /api/upload/file/avatars/...
  if (cleanPath.startsWith("/public/")) {
    const pathWithoutPublic = cleanPath.replace("/public/", "");
    const pathParts = pathWithoutPublic.split("/");
    if (pathParts.length >= 2) {
      const subfolder = pathParts[0];
      const fileName = pathParts.slice(1).join("/");
      cleanPath = `/api/upload/file/${subfolder}/${fileName}`;
    }
  }

  // Normalize API_URL để tránh double /api/api
  // Nếu API_URL đã có /api ở cuối và cleanPath cũng bắt đầu với /api, thì remove /api từ API_URL
  let normalizedApiUrl = API_URL;
  if (cleanPath.startsWith("/api/") && normalizedApiUrl.endsWith("/api")) {
    normalizedApiUrl = normalizedApiUrl.replace(/\/api$/, "");
  }

  return `${normalizedApiUrl}${cleanPath}`;
};

// Helper function để fetch image với Authorization header và convert sang data URI
export const fetchImageAsDataUri = async (
  imageUrl: string
): Promise<string | null> => {
  try {
    const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    if (!token) {
      return null;
    }

    const response = await fetch(imageUrl, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      credentials: "include",
    });

    if (!response.ok) {
      return null;
    }

    // Convert response thành blob, sau đó thành data URI
    const blob = await response.blob();

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const dataUri = reader.result as string;
        resolve(dataUri);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    return null;
  }
};

export function AvatarProvider({ children }: { children: React.ReactNode }) {
  const { user, isLoading: authLoading } = useAuth();
  const [avatarUri, setAvatarUriState] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load avatar khi user thay đổi (đăng nhập/đăng xuất/đổi tài khoản)
  // Đợi auth loading xong trước khi load avatar
  useEffect(() => {
    if (!authLoading) {
      loadAvatar();
    }
  }, [user?.id, authLoading]); // Load lại khi user ID thay đổi hoặc auth loading xong

  const loadAvatar = async () => {
    try {
      // Clear avatar cũ khi user thay đổi - QUAN TRỌNG: Clear ngay lập tức
      setAvatarUriState(null);
      setIsLoading(true);

      // Nếu không có user, clear avatar và clear tất cả storage keys cũ
      if (!user?.id) {
        // Clear tất cả avatar storage keys (cleanup)
        const keysToRemove: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && key.startsWith("hrm_avatar_uri_")) {
            keysToRemove.push(key);
          }
        }
        keysToRemove.forEach((key) => localStorage.removeItem(key));
        setAvatarUriState(null);
        setIsLoading(false);
        return;
      }

      const userId =
        user?.id !== undefined && user?.id !== null ? String(user.id) : null;
      const storageKey = getAvatarStorageKey(userId);

      // QUAN TRỌNG: Clear avatar của các user khác (chỉ giữ lại của user hiện tại)
      // Điều này đảm bảo mỗi user chỉ thấy avatar của mình
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith("hrm_avatar_uri_") && key !== storageKey) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((key) => {
        localStorage.removeItem(key);
      });

      // Thử load từ storage trước (cho user hiện tại) - nhưng chỉ dùng nếu là data URI
      const storedUri = localStorage.getItem(storageKey);
      if (storedUri && storedUri.startsWith("data:")) {
        setAvatarUriState(storedUri);
      }

      // Sau đó thử load từ server profile để có avatar mới nhất (đồng bộ với mobile)
      try {
        const profile = await usersService.getProfile();

        if (profile?.avatar) {
          // Build full URL nếu cần
          const fullUrl = buildFullUrl(profile.avatar);

          if (fullUrl) {
            // Fetch image với Authorization header và convert sang data URI
            const dataUri = await fetchImageAsDataUri(fullUrl);

            if (dataUri) {
              // Lưu data URI vào storage
              localStorage.setItem(storageKey, dataUri);
              setAvatarUriState(dataUri);
            } else {
              // Fallback: dùng URL trực tiếp (có thể không hoạt động nếu cần auth)
              localStorage.setItem(storageKey, fullUrl);
              setAvatarUriState(fullUrl);
            }
          }
        } else {
          // Nếu không có avatar trong profile, clear storage
          localStorage.removeItem(storageKey);
          setAvatarUriState(null);
        }
      } catch (profileError) {
        // Nếu không lấy được profile (chưa đăng nhập hoặc lỗi), giữ nguyên storedUri nếu có
        if (storedUri && storedUri.startsWith("data:")) {
          // Giữ lại data URI nếu có
          setAvatarUriState(storedUri);
        } else {
          setAvatarUriState(null);
        }
      }
    } catch (error) {
      setAvatarUriState(null);
    } finally {
      setIsLoading(false);
    }
  };

  const setAvatarUri = async (uri: string | null) => {
    try {
      if (!user?.id) {
        return;
      }

      const storageKey = getAvatarStorageKey(String(user.id));

      if (uri) {
        localStorage.setItem(storageKey, uri);
      } else {
        localStorage.removeItem(storageKey);
      }
      setAvatarUriState(uri);
    } catch (error) {
      // Ignore error
    }
  };

  const refreshAvatar = async () => {
    await loadAvatar();
  };

  const value: AvatarContextType = {
    avatarUri,
    isLoading,
    setAvatarUri,
    refreshAvatar,
  };

  return (
    <AvatarContext.Provider value={value}>{children}</AvatarContext.Provider>
  );
}

export function useAvatar(): AvatarContextType {
  const context = useContext(AvatarContext);
  if (context === undefined) {
    throw new Error("useAvatar must be used within AvatarProvider");
  }
  return context;
}
